import { Button, Tooltip } from '@mui/material';
import EmailIcon from 'crono-fe-common/icons/Icon-Email';
import { TaskTodo } from 'crono-fe-common/types/cronoTaskTodo';
import { replaceAllTemplateVariables } from 'crono-fe-common/utils';
import { withMeta } from 'crono-fe-common/types/mutationMeta';
import { useAnalytics } from 'context/analytics';
import { useAuth } from 'context/auth';
import useEmailIntegrationGuard from 'hooks/guards/useEmailIntegrationGuard';
import useGetAccount from 'hooks/services/account/useGetAccount';
import useSendPreviewEmail from 'hooks/services/email/useSendPreviewEmail';
import useGetTemplateVariableExternalProperties, {
  useIsTemplateVariableExternalPropertiesLoading,
} from 'hooks/services/externalProperty/useGetTemplateVariableExternalProperties';
import useGetProspect from 'hooks/services/prospect/useGetProspect';
import useGetTemplateAttachments from 'hooks/services/templates/useGetTemplateAttachments';
import useGetTemplateVariables from 'hooks/services/variables/useGetTemplateVariables';
import { PersonalizationIdentity } from 'hooks/strategy/usePersonalizationIdentity';
import { formatHtmlContent } from 'utils/formatUtils';

interface SendTestEmailOptionProps {
  task: TaskTodo;
  subject: string;
  personalization?: PersonalizationIdentity;
  onClose: () => void;
}

const SendTestEmailOption = ({
  task,
  subject,
  personalization,
  onClose,
}: SendTestEmailOptionProps) => {
  const { user } = useAuth();
  const analytics = useAnalytics();

  //Same arguments the step's EditableTemplate uses, so these resolve from the
  //React Query cache instead of firing a second set of requests
  const { data: prospectData, isInitialLoading: isGetProspectInitialLoading } =
    useGetProspect(task.prospectId, true, false, false);
  const { data: accountData, isInitialLoading: isGetAccountInitialLoading } =
    useGetAccount(task.accountId ?? undefined);
  const {
    data: templateVariables,
    isInitialLoading: isGetTemplateVariablesInitialLoading,
  } = useGetTemplateVariables(personalization?.onBehalfUserId);
  const externalProperties = useGetTemplateVariableExternalProperties();
  const isGetExternalPropertyInitialLoading =
    useIsTemplateVariableExternalPropertiesLoading();
  const {
    data: templateAttachments,
    isInitialLoading: isGetTemplateAttachmentsInitialLoading,
  } = useGetTemplateAttachments(task.template?.id ?? null);

  //`isInitialLoading` and not `isLoading`: a query left disabled for want of an
  //argument (a step with no contact, a template with no files) keeps reporting
  //the loading status forever, and would latch the button off
  const isPreviewDataLoading =
    isGetProspectInitialLoading ||
    isGetAccountInitialLoading ||
    isGetTemplateVariablesInitialLoading ||
    isGetTemplateAttachmentsInitialLoading ||
    isGetExternalPropertyInitialLoading;

  const {
    isSuccess: emailIntegrated,
    isLoading: isEmailIntegrationGuardLoading,
  } = useEmailIntegrationGuard({
    verifyDefault: true,
  });

  const { mutate: sendPreviewEmail, isLoading: isSendPreviewEmailLoading } =
    useSendPreviewEmail();

  const resolveVariables = (text: string) =>
    replaceAllTemplateVariables(
      text,
      prospectData?.data?.data ?? null,
      accountData?.data?.data ?? null,
      personalization?.user ?? user ?? null,
      templateVariables?.data?.data ?? null,
      externalProperties,
      true,
    );

  const handleSendTestEmail = () => {
    sendPreviewEmail(
      withMeta(
        {
          emailInfo: {
            subject: resolveVariables(subject),
            contentHtml: resolveVariables(
              formatHtmlContent(task.template?.content ?? ''),
            ),
          },
          //A sequence step cannot add its own files, so everything it sends is
          //already stored against the template
          attachmentIds: (templateAttachments?.data?.data ?? []).map(
            (attachment) => attachment.id,
          ),
        },
        {
          successMessage: 'Test email was sent successfully',
          errorMessage: 'Failed to send test email',
        },
      ),
    );

    if (analytics) {
      analytics.track('send-email-preview', {});
    }

    onClose();
  };

  const getTooltipTitle = () => {
    if (isPreviewDataLoading || isEmailIntegrationGuardLoading) {
      return 'Preparing the email preview';
    }
    if (!emailIntegrated) {
      return 'Please set a default email and connect it to send a test email';
    }
    return '';
  };

  return (
    <Tooltip title={getTooltipTitle()} arrow>
      <span className="flex flex-col">
        <Button
          className="option-button"
          endIcon={<EmailIcon />}
          onClick={handleSendTestEmail}
          disabled={
            !emailIntegrated ||
            isPreviewDataLoading ||
            isSendPreviewEmailLoading
          }
        >
          Send test email
        </Button>
      </span>
    </Tooltip>
  );
};

export default SendTestEmailOption;
